import { useState } from 'react'
import { usePageTitle } from '@components/layout/MainLayout'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material'
import {
  EmojiEvents,
  TrendingUp,
  Schedule,
  CalendarMonth,
  LocationOn,
  Star,
} from '@mui/icons-material'
import { useAuthStore } from '@store/authStore'

interface LeaderboardEntry {
  rank: number
  userId: string
  username: string
  displayName: string
  avatarUrl?: string
  score: number
  adventuresCompleted: number
  badgesEarned: number
  level: number
  location?: string
  isCurrentUser?: boolean
}

type LeaderboardType = 'global' | 'local' | 'friends'
type TimeRange = 'all' | 'month' | 'week'

// Mock leaderboard data
const mockLeaderboard: LeaderboardEntry[] = [
  {
    rank: 1,
    userId: '1',
    username: 'trail_master_99',
    displayName: 'Sarah Trail',
    score: 15420,
    adventuresCompleted: 127,
    badgesEarned: 45,
    level: 28,
    location: 'San Francisco, CA',
  },
  {
    rank: 2,
    userId: '2',
    username: 'urban_explorer',
    displayName: 'Mike Urban',
    score: 14890,
    adventuresCompleted: 115,
    badgesEarned: 42,
    level: 26,
    location: 'San Francisco, CA',
  },
  {
    rank: 3,
    userId: '3',
    username: 'nature_wanderer',
    displayName: 'Emma Woods',
    score: 13750,
    adventuresCompleted: 108,
    badgesEarned: 39,
    level: 25,
    location: 'Oakland, CA',
  },
  {
    rank: 4,
    userId: '4',
    username: 'history_buff',
    displayName: 'John Historical',
    score: 12940,
    adventuresCompleted: 98,
    badgesEarned: 37,
    level: 24,
    location: 'Berkeley, CA',
    isCurrentUser: true,
  },
  {
    rank: 5,
    userId: '5',
    username: 'cache_hunter',
    displayName: 'Lisa Cache',
    score: 11850,
    adventuresCompleted: 92,
    badgesEarned: 34,
    level: 22,
    location: 'San Jose, CA',
  },
  {
    rank: 6,
    userId: '6',
    username: 'street_artist',
    displayName: 'Carlos Art',
    score: 10920,
    adventuresCompleted: 85,
    badgesEarned: 31,
    level: 21,
    location: 'San Francisco, CA',
  },
  {
    rank: 7,
    userId: '7',
    username: 'food_adventurer',
    displayName: 'Rachel Foodie',
    score: 10150,
    adventuresCompleted: 79,
    badgesEarned: 28,
    level: 20,
    location: 'Palo Alto, CA',
  },
  {
    rank: 8,
    userId: '8',
    username: 'photo_seeker',
    displayName: 'David Lens',
    score: 9680,
    adventuresCompleted: 74,
    badgesEarned: 26,
    level: 19,
    location: 'San Francisco, CA',
  },
  {
    rank: 9,
    userId: '9',
    username: 'bridge_walker',
    displayName: 'Amy Bridges',
    score: 9120,
    adventuresCompleted: 68,
    badgesEarned: 24,
    level: 18,
    location: 'Sausalito, CA',
  },
  {
    rank: 10,
    userId: '10',
    username: 'sunset_chaser',
    displayName: 'Tom Golden',
    score: 8570,
    adventuresCompleted: 62,
    badgesEarned: 22,
    level: 17,
    location: 'San Francisco, CA',
  },
]

export default function LeaderboardPage() {
  usePageTitle('Leaderboard')
  const [leaderboardType, setLeaderboardType] = useState<LeaderboardType>('global')
  const [timeRange, setTimeRange] = useState<TimeRange>('all')
  const { user } = useAuthStore()

  const currentUserEntry = mockLeaderboard.find((entry) => entry.isCurrentUser)
  const topThree = mockLeaderboard.slice(0, 3)

  const getMedalColor = (rank: number) => {
    switch (rank) {
      case 1:
        return '#FFD700' // Gold
      case 2:
        return '#C0C0C0' // Silver
      case 3:
        return '#CD7F32' // Bronze
      default:
        return 'text.secondary'
    }
  }

  const getMedalIcon = (rank: number) => {
    if (rank <= 3) {
      return <EmojiEvents sx={{ color: getMedalColor(rank), fontSize: 28 }} />
    }
    return <Typography fontWeight="bold" color="text.secondary">#{rank}</Typography>
  }

  return (
    <Box>
      {/* Top 3 Podium */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
          Top Adventurers
        </Typography>
        <Grid container spacing={3} sx={{ alignItems: 'flex-end' }}>
          {/* 2nd Place */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                textAlign: 'center',
                border: '3px solid',
                borderColor: getMedalColor(2),
                height: 280,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <CardContent>
                <EmojiEvents sx={{ fontSize: 48, color: getMedalColor(2), mb: 2 }} />
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    border: '3px solid',
                    borderColor: getMedalColor(2),
                  }}
                >
                  {topThree[1]?.displayName[0]}
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  {topThree[1]?.displayName}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  @{topThree[1]?.username}
                </Typography>
                <Chip
                  label={`${topThree[1]?.score.toLocaleString()} pts`}
                  color="default"
                  sx={{ mt: 1, fontWeight: 'bold' }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* 1st Place */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                textAlign: 'center',
                border: '4px solid',
                borderColor: getMedalColor(1),
                height: 320,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                boxShadow: 6,
              }}
            >
              <CardContent>
                <EmojiEvents sx={{ fontSize: 64, color: getMedalColor(1), mb: 2 }} />
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    mx: 'auto',
                    mb: 2,
                    border: '4px solid',
                    borderColor: getMedalColor(1),
                  }}
                >
                  {topThree[0]?.displayName[0]}
                </Avatar>
                <Typography variant="h5" fontWeight="bold">
                  {topThree[0]?.displayName}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  @{topThree[0]?.username}
                </Typography>
                <Chip
                  label={`${topThree[0]?.score.toLocaleString()} pts`}
                  sx={{
                    mt: 1,
                    fontWeight: 'bold',
                    backgroundColor: getMedalColor(1),
                    color: 'white',
                  }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* 3rd Place */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                textAlign: 'center',
                border: '3px solid',
                borderColor: getMedalColor(3),
                height: 260,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <CardContent>
                <EmojiEvents sx={{ fontSize: 40, color: getMedalColor(3), mb: 2 }} />
                <Avatar
                  sx={{
                    width: 70,
                    height: 70,
                    mx: 'auto',
                    mb: 2,
                    border: '3px solid',
                    borderColor: getMedalColor(3),
                  }}
                >
                  {topThree[2]?.displayName[0]}
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  {topThree[2]?.displayName}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  @{topThree[2]?.username}
                </Typography>
                <Chip
                  label={`${topThree[2]?.score.toLocaleString()} pts`}
                  color="default"
                  sx={{ mt: 1, fontWeight: 'bold' }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Tabs
              value={leaderboardType}
              onChange={(_, newValue) => setLeaderboardType(newValue)}
            >
              <Tab label="Global" value="global" />
              <Tab label="Local" value="local" />
              <Tab label="Friends" value="friends" />
            </Tabs>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ToggleButtonGroup
                value={timeRange}
                exclusive
                onChange={(_, newValue) => newValue && setTimeRange(newValue)}
                size="small"
              >
                <ToggleButton value="all">
                  <Schedule sx={{ mr: 1 }} fontSize="small" />
                  All Time
                </ToggleButton>
                <ToggleButton value="month">
                  <CalendarMonth sx={{ mr: 1 }} fontSize="small" />
                  This Month
                </ToggleButton>
                <ToggleButton value="week">
                  <TrendingUp sx={{ mr: 1 }} fontSize="small" />
                  This Week
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Current User Stats (if not in top 3) */}
      {currentUserEntry && currentUserEntry.rank > 3 && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            border: '2px solid',
            borderColor: 'primary.main',
            backgroundColor: 'primary.50',
          }}
        >
          <Typography variant="subtitle2" color="primary" gutterBottom>
            Your Ranking
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Typography variant="h4" fontWeight="bold">
                #{currentUserEntry.rank}
              </Typography>
            </Grid>
            <Grid item xs>
              <Typography variant="body2" fontWeight="bold">
                {currentUserEntry.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {currentUserEntry.score.toLocaleString()} points • Level {currentUserEntry.level}
              </Typography>
            </Grid>
            <Grid item>
              <Chip
                label={`${currentUserEntry.adventuresCompleted} Adventures`}
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid item>
              <Chip
                label={`${currentUserEntry.badgesEarned} Badges`}
                size="small"
                variant="outlined"
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Full Leaderboard Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Rank</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Adventurer</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Level</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                Adventures
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                Badges
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                Score
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mockLeaderboard.map((entry) => (
              <TableRow
                key={entry.userId}
                sx={{
                  backgroundColor: entry.isCurrentUser ? 'primary.50' : 'inherit',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getMedalIcon(entry.rank)}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ width: 40, height: 40 }}>{entry.displayName[0]}</Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {entry.displayName}
                        {entry.isCurrentUser && (
                          <Chip label="You" size="small" color="primary" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        @{entry.username}
                      </Typography>
                      {entry.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {entry.location}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={`Lvl ${entry.level}`} size="small" color="secondary" />
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="medium">{entry.adventuresCompleted}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="medium">{entry.badgesEarned}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold" color="primary">
                    {entry.score.toLocaleString()}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
